import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Layers, Flame, RotateCcw, Box, ZoomIn, ZoomOut, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';
import * as THREE from 'three';

// Enhanced Three.js 3D Scene Component with full interaction
function ThreeScene({ shops, selectedFloor, showHeatmap, heatmapData, selectedShop, onShopClick }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const shopMeshesRef = useRef({});
  const animationIdRef = useRef(null);

  // Get shop color based on status or heatmap
  const getShopColor = (shop) => {
    if (showHeatmap) {
      const heat = heatmapData?.find(h => h.shop_id === shop.id);
      const value = heat ? heat.footfall / 500 : Math.random();
      if (value > 0.7) return 0xef4444;
      if (value > 0.4) return 0xeab308;
      return 0x3b82f6;
    }
    switch (shop.status) {
      case 'occupied': return 0x10b981;
      case 'vacant': return 0xf59e0b;
      case 'reserved': return 0x3b82f6;
      default: return 0x6b7280;
    }
  };

  // Build shops function
  const buildScene = () => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Clear everything except lights
    const toKeep = scene.children.filter(c => c.type.includes('Light'));
    scene.children.length = 0;
    toKeep.forEach(c => scene.add(c));
    shopMeshesRef.current = {};

    if (!shops || shops.length === 0) return;

    // Get floor numbers
    const floorNumbers = [...new Set(shops.map(s => s.floor))].sort((a, b) => a - b);
    const filteredFloors = selectedFloor === 'all' ? floorNumbers : [parseInt(selectedFloor)];
    const floorHeight = 6;

    filteredFloors.forEach((floor) => {
      const floorY = floor * floorHeight;

      // Floor base
      const floorGeo = new THREE.PlaneGeometry(70, 50);
      const floorMat = new THREE.MeshStandardMaterial({ 
        color: 0x1a1a2e, 
        transparent: true, 
        opacity: 0.95,
      });
      const floorMesh = new THREE.Mesh(floorGeo, floorMat);
      floorMesh.rotation.x = -Math.PI / 2;
      floorMesh.position.set(0, floorY - 0.1, 0);
      floorMesh.receiveShadow = true;
      scene.add(floorMesh);

      // Grid
      const grid = new THREE.GridHelper(70, 14, 0x00d4ff, 0x1a3a4a);
      grid.position.y = floorY;
      scene.add(grid);

      // Shops
      const floorShops = shops.filter(s => s.floor === floor);
      const cols = 8;
      const shopW = 3.5, shopD = 3, shopH = 2.5, gapX = 1, gapZ = 1.5;
      const totalW = cols * (shopW + gapX) - gapX;
      const startX = -totalW / 2 + shopW / 2;

      floorShops.forEach((shop, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;
        
        const x = startX + col * (shopW + gapX);
        const z = row * (shopD + gapZ) - 12;
        const y = floorY + shopH / 2;

        const geo = new THREE.BoxGeometry(shopW, shopH, shopD);
        const color = getShopColor(shop);
        const isSelected = selectedShop?.id === shop.id;
        
        const mat = new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: isSelected ? 0.5 : 0.1,
          roughness: 0.3,
          metalness: 0.2,
        });
        
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(x, y, z);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData = { ...shop, baseY: y };
        
        if (isSelected) {
          mesh.scale.setScalar(1.15);
        }
        
        scene.add(mesh);
        shopMeshesRef.current[shop.id] = mesh;
      });
    });
  };

  // Main initialization effect
  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up any existing canvas first
    const existingCanvas = containerRef.current.querySelector('canvas');
    if (existingCanvas) {
      containerRef.current.removeChild(existingCanvas);
    }

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a1a);
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      45,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;
    
    // Camera orbit controls
    let theta = Math.PI / 4;
    let phi = Math.PI / 3.5;
    let radius = 80;
    
    const updateCamera = () => {
      camera.position.x = radius * Math.sin(phi) * Math.cos(theta);
      camera.position.y = radius * Math.cos(phi) + 10;
      camera.position.z = radius * Math.sin(phi) * Math.sin(theta);
      camera.lookAt(0, 5, 0);
    };
    updateCamera();

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    
    // Add canvas with proper styling for event handling
    const canvas = renderer.domElement;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.cursor = 'grab';
    canvas.setAttribute('data-testid', '3d-canvas');
    containerRef.current.appendChild(canvas);
    rendererRef.current = renderer;

    // Add lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(30, 50, 30);
    dirLight.castShadow = true;
    scene.add(dirLight);

    const p1 = new THREE.PointLight(0x4cc9f0, 0.6, 150);
    p1.position.set(-30, 40, -30);
    scene.add(p1);

    const p2 = new THREE.PointLight(0xf72585, 0.4, 150);
    p2.position.set(30, 40, -30);
    scene.add(p2);

    // Build initial scene
    buildScene();

    // Raycaster for click detection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    
    // Mouse interaction state - use refs to persist across renders
    let isDragging = false;
    let mouseDownPos = { x: 0, y: 0 };
    let lastMousePos = { x: 0, y: 0 };
    
    const onMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging = true;
      mouseDownPos = { x: e.clientX, y: e.clientY };
      lastMousePos = { x: e.clientX, y: e.clientY };
      renderer.domElement.style.cursor = 'grabbing';
    };
    
    const onMouseUp = (e) => {
      e.preventDefault();
      const moveDistance = Math.hypot(
        e.clientX - mouseDownPos.x,
        e.clientY - mouseDownPos.y
      );
      
      // Only trigger click if mouse didn't move much
      if (moveDistance < 5) {
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        
        raycaster.setFromCamera(mouse, camera);
        const meshes = Object.values(shopMeshesRef.current);
        const intersects = raycaster.intersectObjects(meshes);
        
        if (intersects.length > 0 && intersects[0].object.userData?.id) {
          onShopClick(intersects[0].object.userData);
        }
      }
      
      isDragging = false;
      renderer.domElement.style.cursor = 'grab';
    };
    
    const onMouseMove = (e) => {
      if (!isDragging) return;
      
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      
      // More responsive rotation (increased multiplier)
      theta -= deltaX * 0.015;
      phi = Math.max(0.3, Math.min(Math.PI / 2.2, phi + deltaY * 0.015));
      
      lastMousePos = { x: e.clientX, y: e.clientY };
      updateCamera();
    };
    
    const onMouseLeave = () => {
      isDragging = false;
      renderer.domElement.style.cursor = 'grab';
    };
    
    const onWheel = (e) => {
      e.preventDefault();
      radius = Math.max(30, Math.min(150, radius + e.deltaY * 0.1));
      updateCamera();
    };
    
    // Touch support for mobile
    let touchStart = { x: 0, y: 0 };
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        lastMousePos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    
    const onTouchMove = (e) => {
      if (e.touches.length === 1) {
        const deltaX = e.touches[0].clientX - lastMousePos.x;
        const deltaY = e.touches[0].clientY - lastMousePos.y;
        
        theta -= deltaX * 0.01;
        phi = Math.max(0.3, Math.min(Math.PI / 2.2, phi + deltaY * 0.01));
        
        lastMousePos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        updateCamera();
      }
    };

    // Add event listeners (canvas already defined from renderer setup)
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    
    // Prevent context menu
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Resize
    const onResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', onResize);

    // Animation
    const clock = new THREE.Clock();
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();
      
      // Animate selected shop
      if (selectedShop && shopMeshesRef.current[selectedShop.id]) {
        const mesh = shopMeshesRef.current[selectedShop.id];
        mesh.position.y = mesh.userData.baseY + Math.sin(time * 3) * 0.3;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationIdRef.current);
      const canvas = renderer.domElement;
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', onResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      sceneRef.current = null;
      rendererRef.current = null;
      cameraRef.current = null;
    };
  }, []);

  // Rebuild when props change
  useEffect(() => {
    buildScene();
  }, [shops, selectedFloor, showHeatmap, heatmapData, selectedShop]);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full"
      style={{ cursor: 'grab' }}
    />
  );
}

export default function DigitalTwinPage() {
  const { mallId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [malls, setMalls] = useState([]);
  const [selectedMallId, setSelectedMallId] = useState(mallId || '');
  const [shops, setShops] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedShop, setSelectedShop] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [viewMode, setViewMode] = useState('2d');

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (selectedMallId) {
      fetchShops();
      fetchHeatmap();
    }
  }, [selectedMallId]);

  const fetchMalls = async () => {
    try {
      const response = await api.get('/malls');
      setMalls(response.data);
      if (!mallId && response.data.length > 0) {
        setSelectedMallId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch malls:', error);
      toast.error('Failed to load malls');
    }
  };

  const fetchShops = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/shops?mall_id=${selectedMallId}`);
      setShops(response.data);
    } catch (error) {
      console.error('Failed to fetch shops:', error);
      toast.error('Failed to load shop data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHeatmap = async () => {
    try {
      const response = await api.get(`/analytics/mall/${selectedMallId}/heatmap`);
      setHeatmapData(response.data.heatmap_data || []);
    } catch (error) {
      console.error('Failed to fetch heatmap:', error);
    }
  };

  const handleMallChange = (value) => {
    setSelectedMallId(value);
    setSelectedShop(null);
    setSelectedFloor('all');
    navigate(`/digital-twin/${value}`, { replace: true });
  };

  const floors = [...new Set(shops.map(s => s.floor))].sort((a, b) => a - b);
  const selectedMall = malls.find(m => m.id === selectedMallId);

  const filteredShops = selectedFloor === 'all' 
    ? shops 
    : shops.filter(s => s.floor === parseInt(selectedFloor));

  const getShopColorClass = (shop) => {
    if (showHeatmap) {
      const heat = heatmapData.find(h => h.shop_id === shop.id);
      const value = heat ? heat.footfall / 500 : Math.random();
      if (value > 0.7) return 'bg-red-500';
      if (value > 0.4) return 'bg-yellow-500';
      return 'bg-blue-500';
    }
    switch (shop.status) {
      case 'occupied': return 'bg-emerald-500';
      case 'vacant': return 'bg-amber-500';
      case 'reserved': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const render2DView = () => (
    <div 
      className="p-8 min-h-full flex items-center justify-center"
      style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
    >
      <div className="relative" style={{ width: '800px', height: '500px' }}>
        <div className="absolute inset-0 rounded-xl border border-primary/20 bg-primary/5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(hsl(var(--primary) / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {filteredShops.map((shop, index) => {
          const cols = 10;
          const row = Math.floor(index / cols);
          const col = index % cols;
          const x = 40 + col * 70;
          const y = 40 + row * 55;
          
          return (
            <div
              key={shop.id}
              className={`absolute cursor-pointer transition-all duration-200 rounded-lg border-2 ${
                selectedShop?.id === shop.id 
                  ? 'border-primary shadow-lg shadow-primary/30 z-10' 
                  : 'border-transparent hover:border-primary/50'
              } ${getShopColorClass(shop)} ${selectedShop?.id === shop.id ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              style={{
                left: `${x}px`,
                top: `${y}px`,
                width: '60px',
                height: '45px',
              }}
              onClick={() => setSelectedShop(shop)}
              data-testid={`shop-${shop.id}`}
            >
              <div className="flex flex-col items-center justify-center h-full text-white text-xs">
                <span className="font-mono font-bold truncate px-1">{shop.unit_number.slice(-3)}</span>
                {shop.brand_name && (
                  <span className="text-[10px] truncate px-1 opacity-80">{shop.brand_name.slice(0, 6)}</span>
                )}
              </div>
            </div>
          );
        })}

        <div className="absolute top-2 left-4 text-xs text-primary/60 font-mono">
          {selectedFloor === 'all' ? 'All Floors' : `Floor ${selectedFloor}`} • {filteredShops.length} units
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="digital-twin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Box className="w-6 h-6 text-primary" />
            Digital Twin
          </h2>
          <p className="text-muted-foreground">Interactive 3D mall visualization</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedMallId} onValueChange={handleMallChange}>
            <SelectTrigger className="w-[250px] input-dark" data-testid="select-mall">
              <SelectValue placeholder="Select a mall" />
            </SelectTrigger>
            <SelectContent>
              {malls.map(mall => (
                <SelectItem key={mall.id} value={mall.id}>
                  {mall.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Floor Plan Viewer */}
        <div className="lg:col-span-3">
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-0">
              <div className="h-[600px] bg-gradient-to-b from-gray-900/80 to-black overflow-hidden" data-testid="floor-plan">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : viewMode === '3d' ? (
                  <ThreeScene
                    shops={shops}
                    selectedFloor={selectedFloor}
                    showHeatmap={showHeatmap}
                    heatmapData={heatmapData}
                    selectedShop={selectedShop}
                    onShopClick={setSelectedShop}
                  />
                ) : (
                  <div className="h-full overflow-auto">
                    {render2DView()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Controls */}
          <Card className="glass-card mt-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 bg-muted/30 rounded-lg p-1">
                  <Button
                    variant={viewMode === '3d' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('3d')}
                    className={viewMode === '3d' ? 'btn-primary' : ''}
                    data-testid="view-3d"
                  >
                    <Box className="w-4 h-4 mr-1" />
                    3D View
                  </Button>
                  <Button
                    variant={viewMode === '2d' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('2d')}
                    className={viewMode === '2d' ? 'btn-primary' : ''}
                    data-testid="view-2d"
                  >
                    <Grid3X3 className="w-4 h-4 mr-1" />
                    2D View
                  </Button>
                </div>

                <div className="h-6 w-px bg-border" />

                {/* Floor Selector */}
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Floor:</span>
                  <div className="flex gap-1">
                    <Button
                      variant={selectedFloor === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedFloor('all')}
                      className={selectedFloor === 'all' ? 'btn-primary' : ''}
                      data-testid="floor-all"
                    >
                      All
                    </Button>
                    {floors.map(floor => (
                      <Button
                        key={floor}
                        variant={selectedFloor === String(floor) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedFloor(String(floor))}
                        className={selectedFloor === String(floor) ? 'btn-primary' : ''}
                        data-testid={`floor-${floor}`}
                      >
                        {floor}
                      </Button>
                    ))}
                  </div>
                </div>

                {viewMode === '2d' && (
                  <>
                    <div className="h-6 w-px bg-border" />
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                        data-testid="zoom-out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-mono w-12 text-center">{Math.round(zoom * 100)}%</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                        data-testid="zoom-in"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}

                <div className="h-6 w-px bg-border" />

                <Button
                  variant={showHeatmap ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowHeatmap(!showHeatmap)}
                  className={showHeatmap ? 'btn-primary' : ''}
                  data-testid="toggle-heatmap"
                >
                  <Flame className="w-4 h-4 mr-1" />
                  Heatmap
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedFloor('all');
                    setSelectedShop(null);
                    setShowHeatmap(false);
                    setZoom(1);
                  }}
                  data-testid="reset-view"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              </div>

              {viewMode === '3d' && (
                <div className="mt-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
                  <span className="font-medium text-primary">3D Controls:</span> Drag to rotate • Scroll to zoom • Click shops to select • Hover for preview
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Panel */}
        <div className="space-y-4">
          {/* Legend */}
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Legend</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {showHeatmap ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500" />
                    <span className="text-sm">High Footfall</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500" />
                    <span className="text-sm">Medium Footfall</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="text-sm">Low Footfall</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-emerald-500" />
                    <span className="text-sm">Occupied</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-500" />
                    <span className="text-sm">Vacant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="text-sm">Reserved</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Mall Stats */}
          {selectedMall && (
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{selectedMall.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Shops</span>
                  <span className="font-mono">{shops.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Floors</span>
                  <span className="font-mono">{floors.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Occupancy</span>
                  <span className="font-mono">{selectedMall.occupancy_rate?.toFixed(1)}%</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Selected Shop Info */}
          {selectedShop && (
            <Card className="glass-card border-primary/50 animate-scale-in">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Shop Details</CardTitle>
                  <Badge
                    className={`status-badge ${
                      selectedShop.status === 'occupied'
                        ? 'status-occupied'
                        : selectedShop.status === 'vacant'
                        ? 'status-vacant'
                        : 'status-reserved'
                    }`}
                  >
                    {selectedShop.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Unit Number</p>
                  <p className="font-mono font-medium">{selectedShop.unit_number}</p>
                </div>
                {selectedShop.brand_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">Brand</p>
                    <p className="font-medium">{selectedShop.brand_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Floor</p>
                  <p className="font-mono">Floor {selectedShop.floor}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Category</p>
                  <p className="capitalize">{selectedShop.category.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Area</p>
                  <p className="font-mono">{selectedShop.carpet_area_sqft} sqft</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rent/sqft</p>
                  <p className="font-mono">₹{selectedShop.rent_per_sqft}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Monthly Rent</p>
                  <p className="font-mono text-lg text-primary">₹{selectedShop.monthly_rent?.toLocaleString()}</p>
                </div>
                <div className="pt-2 space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate(`/inventory?mall=${selectedMallId}`)}
                    data-testid="view-in-inventory"
                  >
                    View in Inventory
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={() => setSelectedShop(null)}
                  >
                    Clear Selection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
